import { ArrowLeft, Download, PackageOpen } from 'lucide-react';
import { useEffect } from 'react';
import { CreatorLink } from '../components/CreatorLink';
import { ConnectionBadge } from '../components/ConnectionBadge';
import { DeviceIcon } from '../components/DeviceIcon';
import { Link } from '../components/Link';
import { PackDownloadButton } from '../components/PackDownloadButton';
import { ProfileTable } from '../components/ProfileTable';
import { packsById, profilesForPack } from '../lib/catalog';
import { downloadCountForPack, formatDownloads } from '../lib/downloadCounts';
import { canonicalizeCurrentPath, formatDate, packPath } from '../lib/navigation';

interface PackPageProps {
  id: string;
  notify: (message: string, tone?: 'success' | 'error') => void;
}

export function PackPage({ id, notify }: PackPageProps) {
  const pack = packsById.get(id);

  useEffect(() => {
    if (!pack) return;
    canonicalizeCurrentPath(packPath(pack));
  }, [pack]);

  if (!pack) return <div className="empty-state"><PackageOpen size={34} /><h1>Profile pack not found</h1><p>The pack may have moved or the URL is incomplete.</p><Link className="button secondary" href="/packs">Browse profile packs</Link></div>;
  const profiles = profilesForPack(pack);
  const returningHome = window.history.state?.returnTo === '/';

  return (
    <>
      <Link className="back-link" href={returningHome ? '/' : '/packs'}><ArrowLeft size={15} />{returningHome ? 'Back to home' : 'All profile packs'}</Link>
      <section className="pack-detail-hero">
        <DeviceIcon device={pack.device} />
        <div className="pack-detail-copy"><h1>{pack.name}</h1><p>A community {pack.device} profile pack by <strong><CreatorLink name={pack.creator} /></strong>, containing {pack.profileCount} ready-to-load profiles.</p><div className="tag-row">{pack.connections.map((value) => <ConnectionBadge key={value} connection={value} />)}</div></div>
        <div className="pack-detail-actions"><PackDownloadButton pack={pack} profiles={profiles} notify={notify} className="button primary" /><span><Download size={14} />{formatDownloads(downloadCountForPack(pack))} downloads</span></div>
      </section>
      <dl className="pack-facts">
        <div><dt>Profiles</dt><dd>{pack.profileCount}</dd></div>
        <div><dt>Creator</dt><dd><CreatorLink name={pack.creator} /></dd></div>
        <div><dt>Created On</dt><dd>{formatDate(pack.createdAt)}</dd></div>
        <div><dt>Updated On</dt><dd>{formatDate(pack.latestUpdatedAt)}</dd></div>
      </dl>
      <section className="content-section">
        <div className="section-heading"><div><h2>Profiles in this pack</h2></div></div>
        <ProfileTable profiles={profiles} notify={notify} />
      </section>
    </>
  );
}
